/**
 * Direct Shear result-grid column definitions.
 *
 * Formula tooltips are NO LONGER transcribed here — the header pulls the
 * formula/note from the backend Formula Registry (`useTestFormulas('directShear')`)
 * keyed by `columnKey`, so the tooltip text can never drift from the real calc
 * (FEAT "Formula Registry", 2026-07-15). `columnKey` matches the calc output
 * field / dbField and the registry entry's key.
 */
export interface ResultColumn {
  field: string;
  /** Registry key for the header formula tooltip (matches the backend entry). */
  columnKey: string;
  headerName: string;
  shortName: string;
  dbField: string | null;
  source: 'row' | 'input' | 'calculated';
  editable: boolean;
  width: number;
  decimalPlaces?: number;
}

export const directShearColumns: ResultColumn[] = [
  {
    field: 'SampleInt',
    columnKey: 'SampleInt',
    headerName: 'Interval',
    shortName: 'Int',
    dbField: 'SampleInt',
    source: 'input',
    editable: true,
    width: 55,
    decimalPlaces: 0,
  },
  {
    field: 'HorizontalDisplacement',
    columnKey: 'HorizontalDisplacement',
    headerName: 'Horizontal Displacement',
    shortName: 'Horiz Disp',
    dbField: 'HorizontalDisplacement',
    source: 'input',
    editable: true,
    width: 130,
    decimalPlaces: 6,
  },
  {
    field: 'HorizontalLoad',
    columnKey: 'HorizontalLoad',
    headerName: 'Horizontal Load',
    shortName: 'Horiz Load',
    dbField: 'HorizontalLoad',
    source: 'input',
    editable: true,
    width: 110,
    decimalPlaces: 6,
  },
  {
    field: 'VerticalDisplacement',
    columnKey: 'VerticalDisplacement',
    headerName: 'Vertical Displacement',
    shortName: 'Vert Disp',
    dbField: 'VerticalDisplacement',
    source: 'input',
    editable: true,
    width: 130,
    decimalPlaces: 6,
  },
  {
    field: 'VerticalLoad',
    columnKey: 'VerticalLoad',
    headerName: 'Vertical Load',
    shortName: 'Vert Load',
    dbField: 'VerticalLoad',
    source: 'input',
    editable: true,
    width: 100,
    decimalPlaces: 6,
  },
  {
    field: 'IncrementalHorizontalDisplacement',
    columnKey: 'IncrementalHorizontalDisplacement',
    headerName: 'Incremental Horizontal Displacement',
    shortName: 'Incr H Disp',
    dbField: 'IncrementalHorizontalDisplacement',
    source: 'calculated', editable: true, width: 110, decimalPlaces: 6,
  },
  {
    field: 'HorizontalDisplacementCumulative',
    columnKey: 'HorizontalDisplacementCumulative',
    headerName: 'Horizontal Displacement Cumulative',
    shortName: 'H Disp Cum',
    dbField: 'HorizontalDisplacementCumulative',
    source: 'calculated', editable: true, width: 110, decimalPlaces: 6,
  },
  {
    field: 'IncrementalVerticalDisplacement',
    columnKey: 'IncrementalVerticalDisplacement',
    headerName: 'Incremental Vertical Displacement',
    shortName: 'Incr V Disp',
    dbField: 'IncrementalVerticalDisplacement',
    source: 'calculated', editable: true, width: 110, decimalPlaces: 6,
  },
  {
    field: 'VerticalDisplacementCumulative',
    columnKey: 'VerticalDisplacementCumulative',
    headerName: 'Vertical Displacement Cumulative',
    shortName: 'V Disp Cum',
    dbField: 'VerticalDisplacementCumulative',
    source: 'calculated', editable: true, width: 110, decimalPlaces: 6,
  },
  {
    field: 'DilationAngle',
    columnKey: 'DilationAngle',
    headerName: 'Dilation Angle (°)',
    shortName: 'Dilation ∠',
    dbField: 'DilationAngle',
    source: 'calculated', editable: true, width: 100, decimalPlaces: 6,
  },
  {
    field: 'UncorrectedShearStress',
    columnKey: 'UncorrectedShearStress',
    headerName: 'Uncorrected Shear Stress (kPa)',
    shortName: 'Uncorr Shear',
    dbField: 'UncorrectedShearStress',
    source: 'calculated', editable: true, width: 110, decimalPlaces: 6,
  },
  {
    field: 'UncorrectedNormalStress',
    columnKey: 'UncorrectedNormalStress',
    headerName: 'Uncorrected Normal Stress (kPa)',
    shortName: 'Uncorr Normal',
    dbField: 'UncorrectedNormalStress',
    source: 'calculated', editable: true, width: 110, decimalPlaces: 6,
  },
  {
    field: 'CorrectedShearStress',
    columnKey: 'CorrectedShearStress',
    headerName: 'Corrected Shear Stress (kPa)',
    shortName: 'Corr Shear',
    dbField: 'CorrectedShearStress',
    source: 'calculated', editable: true, width: 110, decimalPlaces: 6,
  },
  {
    field: 'CorrectedNormalStress',
    columnKey: 'CorrectedNormalStress',
    headerName: 'Corrected Normal Stress (kPa)',
    shortName: 'Corr Normal',
    dbField: 'CorrectedNormalStress',
    source: 'calculated', editable: true, width: 110, decimalPlaces: 6,
  },
  {
    field: 'CorrectedFrictionAngle',
    columnKey: 'CorrectedFrictionAngle',
    headerName: 'Corrected Friction Angle (°)',
    shortName: 'Corr Frict ∠',
    dbField: 'CorrectedFrictionAngle',
    source: 'calculated', editable: true, width: 110, decimalPlaces: 6,
  },
];
